import { useMemo } from 'react'
import { UsersModule } from '@/core/modules/users'
import { useCustomTheme } from '@/core/theme'
import { InfoBanner } from '@/ui/components/BannerAlert'
import Typography from '@/ui/components/typography'
import {
    AffectedAssetsTable,
    assetNameColumn,
} from '@ui/drawer/common/AffectedAssetsTable'
import { IssueDrawerLayout } from '@ui/drawer/common/IssueDrawerLayout'
import { IssueDrawerFooter } from '@ui/drawer/common/IssueDrawerFooter'
import { RemediationStepsCard } from '@ui/drawer/common/RemediationStepsCard'
import {
    TSelectableAsset,
    getSelectableAssets,
} from '@ui/drawer/common/useIssueResolution'
import { useIssueDrawer } from '@ui/drawer/common/useIssueDrawer'
import { FdEmailDetailsCard } from './components/FdEmailDetailsCard'
import WarningTriangleIcon from '@/assets/images/warning-triangle.svg?react'

const SEVERITY_WARNINGS: Partial<Record<TIssueSeverity, string>> = {
    critical:
        'WARNING: There is a very high probability that this email is malicious, potentially designed for fraud or malware distribution.',
    important:
        'This email may contain phishing attempts, malicious links, or suspicious attachments.',
    moderate:
        'This email may be a spam, marketing scams, or a phishing attempt. It should be handled carefully to prevent future risks.',
}

const FdIssueDetails = () => {
    const theme = useCustomTheme()
    const { issue, rows, selectedIds, setSelectedIds, resolution } =
        useIssueDrawer<TFraudDefenseIssue, TSelectableAsset>({
            store: UsersModule,
            buildRows: getSelectableAssets,
        })

    const columns = useMemo(() => [assetNameColumn()], [])

    const selectedPayloads = rows
        .filter(({ id }) => selectedIds.includes(id))
        .map(({ payload }) => payload)

    const warningText = SEVERITY_WARNINGS[issue.severity]

    const selectionDisabled = issue.status !== 'open' || resolution.loading

    const footer = issue.status === 'open' && (
        <IssueDrawerFooter
            resolution={resolution}
            selectedCount={selectedIds.length}
            hasSelectableAssets={rows.length > 0}
            selection={{ assets: selectedPayloads }}
        />
    )

    return (
        <IssueDrawerLayout footer={footer}>
            <Typography
                variant="s-regular"
                noWrap={false}
                color={theme.colors.greys[800]}
                whiteSpaceRule="pre-line"
                testId="fd-issue-short-description"
            >
                {issue.short_description}
            </Typography>
            {warningText && (
                <InfoBanner
                    variant="error"
                    dataTestId="fd-warning-banner"
                    icon={
                        <WarningTriangleIcon
                            width={16}
                            height={16}
                            color={theme.colors.semantic.error}
                            data-testid="fd-warning-icon"
                        />
                    }
                    title={warningText}
                />
            )}
            <RemediationStepsCard
                issue={issue}
                title="Immediate actions are required:"
            />
            {issue.email_data && (
                <FdEmailDetailsCard
                    emailData={issue.email_data}
                    recipients={issue.recipients}
                />
            )}
            <AffectedAssetsTable
                testIdPrefix="email-threat-assets"
                rows={rows}
                columns={columns}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                disabled={selectionDisabled}
            />
        </IssueDrawerLayout>
    )
}

export default FdIssueDetails
